/**
 * Хук для вычисления статуса лицевого счета
 * 
 * Определяет статус оплаты счета на основе даты paidUntil и состояния блокировки.
 * Предоставляет дружелюбные сообщения и рекомендации для пользователя.
 */

import { useMemo } from 'react';

/**
 * Статусы оплаты лицевого счета
 */
export enum PaymentStatus {
  /** Оплачено более чем на 7 дней */
  PAID = 'paid',
  /** Скоро истекает (≤7 дней) или уже истекло */
  EXPIRING = 'expiring',
  /** Счет заблокирован */
  BLOCKED = 'blocked',
}

/**
 * Информация о статусе лицевого счета
 */
export interface AccountStatus {
  /** Статус оплаты */
  status: PaymentStatus;
  /** Количество дней до истечения оплаты (может быть отрицательным) */
  daysRemaining: number;
  /** Рекомендуемая сумма пополнения (стоимость тарифа) */
  suggestedAmount: number;
  /** Дружелюбное сообщение для отображения пользователю */
  message: string;
  /** Показывать ли кнопку быстрой оплаты */
  showQuickPay: boolean;
}

/**
 * Параметры для вычисления статуса счета
 */
export interface AccountStatusParams {
  /** Дата, до которой оплачены услуги */
  paidUntil: Date | string | null;
  /** Текущий баланс счета */
  balance: number;
  /** Стоимость тарифа за период */
  tariffCost: number;
  /** Флаг блокировки счета */
  isBlocked: boolean;
}

/**
 * Форматирование даты в дружелюбный формат
 * 
 * @param date - Дата для форматирования
 * @returns Отформатированная строка даты
 */
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Вычисление статуса лицевого счета
 * 
 * @param params - Параметры счета
 * @returns Информация о статусе счета
 */
const calculateAccountStatus = (params: AccountStatusParams): AccountStatus => {
  const { paidUntil, balance, tariffCost, isBlocked } = params;

  // Если счет заблокирован - показываем нейтральное сообщение
  if (isBlocked) {
    return {
      status: PaymentStatus.BLOCKED,
      daysRemaining: 0,
      suggestedAmount: tariffCost,
      message: 'Для возобновления услуг пополните баланс',
      showQuickPay: true,
    };
  }

  // Если нет даты оплаты - рекомендуем пополнить
  if (!paidUntil) {
    return {
      status: PaymentStatus.EXPIRING,
      daysRemaining: 0,
      suggestedAmount: tariffCost,
      message: 'Рекомендуем пополнить баланс для продолжения услуг',
      showQuickPay: true,
    };
  }

  const now = new Date();
  const paidUntilDate = typeof paidUntil === 'string' ? new Date(paidUntil) : paidUntil;
  
  // Вычисляем количество дней до истечения оплаты
  const daysRemaining = Math.ceil(
    (paidUntilDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Оплачено более чем на 7 дней - все хорошо
  if (daysRemaining > 7) {
    return {
      status: PaymentStatus.PAID,
      daysRemaining,
      suggestedAmount: tariffCost,
      message: `Оплачено до ${formatDate(paidUntilDate)}`,
      showQuickPay: false,
    };
  }

  // Осталось 7 дней или меньше (или уже истекло) - дружелюбное напоминание
  const isPastDue = daysRemaining < 0;

  if (isPastDue) {
    return {
      status: PaymentStatus.EXPIRING,
      daysRemaining,
      suggestedAmount: tariffCost,
      message: 'Рекомендуем пополнить баланс для продолжения услуг',
      showQuickPay: true,
    };
  }

  // Осталось мало дней
  return {
    status: PaymentStatus.EXPIRING,
    daysRemaining,
    suggestedAmount: tariffCost,
    message: `Оплачено до ${formatDate(paidUntilDate)}. Рекомендуем пополнить баланс`,
    showQuickPay: true,
  };
};

/**
 * Результат хука useAccountStatus
 */
export interface UseAccountStatusResult extends AccountStatus {
  /** Флаг, указывающий что оплата истекла */
  isPastDue: boolean;
  /** Флаг, указывающий что скоро истечет (≤7 дней) */
  isExpiringSoon: boolean;
  /** Флаг, указывающий что все в порядке */
  isPaid: boolean;
}

/**
 * Хук для вычисления статуса лицевого счета
 * 
 * Определяет статус оплаты на основе даты paidUntil, баланса и состояния блокировки.
 * Предоставляет дружелюбные сообщения без пугающих формулировок.
 * 
 * @param params - Параметры счета для вычисления статуса
 * @returns Информация о статусе счета с дополнительными флагами
 * 
 * @example
 * ```tsx
 * const status = useAccountStatus({
 *   paidUntil: '2025-11-15',
 *   balance: 500,
 *   tariffCost: 600,
 *   isBlocked: false,
 * });
 * 
 * if (status.showQuickPay) {
 *   // Показать кнопку быстрой оплаты
 * }
 * ```
 */
export const useAccountStatus = (params: AccountStatusParams): UseAccountStatusResult => {
  const status = useMemo(() => calculateAccountStatus(params), [
    params.paidUntil,
    params.balance,
    params.tariffCost,
    params.isBlocked,
  ]);

  return useMemo(
    () => ({
      ...status,
      isPastDue: status.daysRemaining < 0,
      isExpiringSoon: status.daysRemaining <= 7 && status.daysRemaining >= 0,
      isPaid: status.status === PaymentStatus.PAID,
    }),
    [status]
  );
};
